import React, { useEffect, useState, useCallback } from "react";
import { Check, Loader2, Sparkles, ArrowDownCircle } from "lucide-react";
import { toast } from "sonner";
import { AppShell } from "@/components/app/AppShell";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { useAuth } from "@/context/AuthContext";
import api, { getErrorMessage } from "@/lib/api";
import { trackEvent } from "@/lib/analytics";
import { BillingToggle } from "@/components/billing/BillingToggle";
import { useBillingOptions, findOption } from "@/lib/billing";

const TIERS = [
  { id: "free", name: "Free", price: "$0", period: "forever", rank: 0, features: ["10 assessments / month", "Basic risk score", "Basic reports", "Single property"], highlight: false },
  { id: "pro", name: "Pro Host", price: "$19", period: "/month", rank: 1, features: ["Unlimited risk assessments", "Downloadable PDF evidence reports", "Evidence Centre + saved reports", "Risk analytics dashboard & trends", "AI risk summaries", "Full assessment history"], highlight: true },
  { id: "pm", name: "Property Manager", price: "$99", period: "/month", rank: 2, features: ["Everything in Pro Host", "Multi-property management", "Portfolio analytics", "Team access & roles", "Advanced reporting"], highlight: false },
];

export default function Pricing() {
  const { user, refreshUser } = useAuth();
  const [loadingPlan, setLoadingPlan] = useState(null);
  const [sub, setSub] = useState(null);
  const [interval, setInterval] = useState("monthly");
  const { options } = useBillingOptions();

  const loadSub = useCallback(async () => {
    await refreshUser();
    try { const r = await api.get("/subscription"); setSub(r.data); }
    catch (err) { console.error("Failed to load subscription", err); }
  }, [refreshUser]);

  useEffect(() => { loadSub(); }, [loadSub]);
  useEffect(() => { trackEvent("pricing_viewed", "/app/pricing"); }, []);

  const currentPlan = sub?.plan || user?.plan || "free";
  const currentRank = TIERS.find((t) => t.id === currentPlan)?.rank ?? 0;

  const changePlan = async (planId) => {
    setLoadingPlan(planId);
    try {
      const res = await api.post("/subscription/change", { plan_id: planId, billing_interval: interval, origin_url: window.location.origin });
      if (res.data.requires_checkout && res.data.url) {
        trackEvent("checkout_started", "/app/pricing");
        window.location.href = res.data.url;
        return;
      }
      toast.success("Your plan change has been scheduled. You keep current access until the period ends.");
      await loadSub();
    } catch (err) {
      toast.error(getErrorMessage(err, "Could not change plan."));
    } finally {
      setLoadingPlan(null);
    }
  };

  return (
    <AppShell title="Pricing & Plans">
      <div className="space-y-6">
        <div className="max-w-2xl">
          <h2 className="font-display text-2xl font-semibold">Choose the plan that protects your portfolio.</h2>
          <p className="mt-2 text-sm text-muted-foreground">
            You're currently on the <span className="font-medium text-foreground">{TIERS.find((t) => t.id === currentPlan)?.name}</span> plan
            {sub?.cancel_at_period_end ? " (cancels at period end)" : ""}. Upgrade or downgrade anytime.
          </p>
        </div>

        <div className="flex flex-col items-start gap-2">
          <BillingToggle value={interval} onChange={setInterval} options={options} savingsPlan="pro" />
          {interval !== "monthly" && (
            <p className="text-xs text-muted-foreground">Paid upfront and billed {findOption(options, "pro", interval)?.interval_label || ""} — renews automatically, cancel anytime.</p>
          )}
        </div>

        <div className="grid gap-5 lg:grid-cols-3">
          {TIERS.map((t) => {
            const current = currentPlan === t.id;
            const isDowngrade = t.rank < currentRank;
            const isUpgrade = t.rank > currentRank;
            const testidSuffix = t.id === "pm" ? "property-manager" : t.id;
            const opt = t.id === "free" ? null : findOption(options, t.id, interval);
            const priceStr = t.id === "free" ? "$0" : (opt ? `$${Number.isInteger(opt.amount) ? opt.amount : opt.amount.toFixed(2)}` : t.price);
            const periodStr = t.id === "free" ? "forever" : (opt ? opt.interval_label : t.period);
            return (
              <div key={t.id} className={`flex flex-col rounded-2xl border bg-card p-6 ${t.highlight ? "border-primary/30 shadow-[0_10px_30px_rgba(15,23,42,0.10)]" : ""} ${current ? "ring-2 ring-primary/30" : ""}`} data-testid={`pricing-tier-${t.id}`}>
                <div className="flex items-center justify-between">
                  {t.highlight && <span className="inline-flex w-fit items-center gap-1 rounded-full bg-primary px-2.5 py-0.5 text-xs font-medium text-primary-foreground"><Sparkles className="h-3 w-3" /> Most popular</span>}
                  {current && <span className="ml-auto rounded-full bg-[hsl(var(--risk-low-bg))] px-2.5 py-0.5 text-xs font-medium text-[hsl(var(--risk-low))]">Current</span>}
                </div>
                <h3 className="mt-3 font-display text-lg font-semibold">{t.name}</h3>
                <div className="mt-2 flex items-baseline gap-1">
                  <span className="font-display text-4xl font-semibold" data-testid={`pricing-price-${t.id}`}>{priceStr}</span>
                  <span className="text-sm text-muted-foreground">{periodStr}</span>
                </div>
                {opt && interval !== "monthly" && (
                  <p className="mt-1 text-xs text-[hsl(var(--risk-low))]">≈ ${opt.per_month_equivalent}/month</p>
                )}
                <ul className="mt-5 flex-1 space-y-2.5 text-sm">
                  {t.features.map((f) => <li key={f} className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 shrink-0 text-[hsl(var(--risk-low))]" /><span>{f}</span></li>)}
                </ul>
                <div className="mt-6">
                  {current ? (
                    <Button variant="outline" className="w-full" disabled data-testid={`pricing-current-${t.id}`}>Current plan</Button>
                  ) : isUpgrade ? (
                    <Button className="w-full" variant={t.highlight ? "default" : "outline"} onClick={() => changePlan(t.id)} disabled={loadingPlan === t.id} data-testid={`pricing-checkout-${testidSuffix}-button`}>
                      {loadingPlan === t.id ? <Loader2 className="h-4 w-4 animate-spin" /> : t.id === "free" ? "Free plan" : `Upgrade to ${t.name}`}
                    </Button>
                  ) : (
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="outline" className="w-full gap-2 text-muted-foreground" disabled={loadingPlan === t.id} data-testid={`pricing-downgrade-${testidSuffix}-button`}>
                          {loadingPlan === t.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <><ArrowDownCircle className="h-4 w-4" /> Switch to {t.name}</>}
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Switch to {t.name}?</AlertDialogTitle>
                          <AlertDialogDescription>
                            {t.id === "free"
                              ? "Your paid plan will be canceled at the end of the current billing period. You keep full access until then, after which your account moves to the Free plan."
                              : `Your plan will change to ${t.name}. ${t.rank > 0 ? "You'll be taken to checkout to confirm the new price." : ""}`}
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Keep current plan</AlertDialogCancel>
                          <AlertDialogAction onClick={() => changePlan(t.id)} data-testid={`confirm-downgrade-${testidSuffix}`}>Confirm</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        <p className="text-xs text-muted-foreground">Payments are processed securely by Stripe. Prices in AUD. Subscriptions renew automatically each month until cancelled.</p>
      </div>
    </AppShell>
  );
}
